import React, { useState } from "react";
import transparentImage from "assets/images/transparent.gif";

export interface LogoProps {
  src: string;
  alt: string;
  imgClassName?: string;
  /**
   * Renders blank space at the reserved size when `src` fails to load, instead of the browser's
   * broken-image glyph. Opt-in: most surfaces would rather show that something is missing than
   * silently render nothing.
   */
  blankOnError?: boolean;
}

export const Logo: React.FC<LogoProps> = ({ src, alt, imgClassName = "", blankOnError = false }) => {
  // Tracks the src that failed rather than a boolean, so a new src is given a fresh chance to load.
  // The *same* URL is never retried — only a different `src` gets another attempt. That matters for
  // direct-to-S3 uploads, where a derivative can 404 briefly while the backend generates it, leaving
  // the placeholder until unmount. Not a regression (the glyph is equally stuck) but a deliberate choice.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const hasFailed = blankOnError && failedSrc === src;

  return (
    <div className="flex w-full h-full items-center">
      <img
        // The placeholder itself loads, so the browser paints neither its broken-image icon nor the
        // alt text — which lets `alt` stay put as the text alternative for assistive tech.
        src={hasFailed ? transparentImage : src}
        alt={alt}
        onError={blankOnError ? () => setFailedSrc(src) : undefined}
        className={`max-h-full max-w-full object-contain ${imgClassName}`}
      />
    </div>
  );
};
